import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Badge } from '../../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { RadioGroup, RadioGroupItem } from '../../../ui/radio-group';
import {
  ArrowLeft, Save, Check, X, Plane, FileText, Users, Settings, MapPin, Calendar, UserPlus, Star, ArrowRight, Search, GripVertical, Plus, Trash2, AlertCircle, Truck, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface TourApprovalViewPageProps {
  onNavigate?: (page: string) => void;
  tourId?: string;
}


const portMasterData = [
  { id: 'njf', name: 'Najaf International', code: 'NJF' },
  { id: 'bgw', name: 'Baghdad International', code: 'BGW' },
  { id: 'bsr', name: 'Basra International', code: 'BSR' }
];

// Mock approved visa applicants
const mockApprovedApplicants = [
  {
    id: '1',
    name: 'Ali Hassan',
    itsNumber: '20123456',
    gender: 'Male',
    age: 45,
    status: 'Approved',
    photo: 'https://images.unsplash.com/photo-1672685667592-0392f458f46f?w=1080',
    familyIndicator: 'F-1',
    applicantType: 'Adult',
    needsAccommodation: true
  },
  {
    id: '2',
    name: 'Fatima Ahmed',
    itsNumber: '20234567',
    gender: 'Female',
    age: 38,
    status: 'Approved',
    photo: 'https://images.unsplash.com/photo-1655249493799-9cee4fe983bb?w=1080',
    familyIndicator: 'F-1',
    applicantType: 'Adult',
    needsAccommodation: true
  },
  {
    id: '3',
    name: 'Mohammed Reza',
    itsNumber: '20345678',
    gender: 'Male',
    age: 52,
    status: 'Approved',
    photo: 'https://images.unsplash.com/photo-1762885590877-0829975f2cc2?w=1080',
    familyIndicator: 'F-2',
    applicantType: 'Adult',
    needsAccommodation: true
  },
  {
    id: '4',
    name: 'Zahra Ali',
    itsNumber: '20456789',
    gender: 'Female',
    age: 41,
    status: 'Approved',
    photo: 'https://images.unsplash.com/photo-1546961329-78bef0414d7c?w=1080',
    familyIndicator: null,
    applicantType: 'Adult',
    needsAccommodation: true
  },
];

const mockServices = [
  { id: 'mawaid', name: 'Mawaid (Meals)', type: 'Mandatory', price: 0 },
  { id: 'accommodation', name: 'Accommodation', type: 'Mandatory', price: 0 },
  { id: 'tour-guide', name: 'Tour Guide', type: 'Included', price: 0 },
  { id: 'transport', name: 'Transport', type: 'Included', price: 0 },
  { id: 'wheelchair', name: 'Wheelchair Assistance', type: 'Optional', price: 50 },
];

// Available Atraafs and Activities for sidebar
const availableTransport = [
  { id: 'trans-1', name: 'Arrival Transfer', description: 'Airport → Faiz · Auto-fill', badge: 'AUTO' },
  { id: 'trans-2', name: 'Departure Transfer', description: 'Faiz → Airport · Auto-fill', badge: 'AUTO' },
  { id: 'trans-3', name: 'Intercity: Kerbala → Najaf', description: '~1.5hr · Coach transfer' },
  { id: 'trans-4', name: 'Intercity: Najaf → Kerbala', description: '~1.5hr · Coach transfer' },
];

const availableMawaid = [
  { id: 'mawaid-1', name: 'Breakfast', description: 'Daily meal service' },
  { id: 'mawaid-2', name: 'Lunch', description: 'Daily meal service' },
  { id: 'mawaid-3', name: 'Dinner', description: 'Daily meal service' },
  { id: 'mawaid-4', name: 'Special Event Meal', description: 'For specific occasions' },
];

const availableZiyarat = [
  { id: 'ziy-1', name: 'Imam Hussain Shrine', description: 'Karbala · 2-3hrs' },
  { id: 'ziy-2', name: 'Abbas Shrine', description: 'Karbala · 1-2hrs' },
  { id: 'ziy-3', name: 'Imam Ali Shrine', description: 'Najaf · 2-3hrs' },
  { id: 'ziy-4', name: 'Wadi-us-Salaam', description: 'Najaf · 1hr' },
  { id: 'ziy-5', name: 'Kufa Mosque', description: 'Kufa · 1.5hrs' },
  { id: 'ziy-6', name: 'Samarra Shrine', description: 'Day trip · 6hrs' },
];

const availableExcursions = [
  { id: 'excur-1', name: 'Baghdad City Tour', description: 'Full day · 8hrs with guide' },
  { id: 'excur-2', name: 'Hilla & Babylon', description: 'Half day · 4hrs historical tour' },
  { id: 'excur-3', name: 'Ukhaider Fortress', description: 'Half day · 3hrs desert castle' },
  { id: 'excur-4', name: 'Local Bazaar Visit', description: 'Evening · 2hrs shopping' },
];

// Venue Master Data
const venueMasterData = [
  { id: 'najaf', name: 'Najaf' },
  { id: 'karbala', name: 'Karbala' },
  { id: 'baghdad', name: 'Baghdad' },
  { id: 'basra', name: 'Basra' },
  { id: 'samarra', name: 'Samarra' },
  { id: 'kufa', name: 'Kufa' },
  { id: 'kadhimiya', name: 'Kadhimiya' },
];

type TabType =
  | 'arrival-departure'
  | 'venue-summary'
  | 'applicants'
  | 'services'
  | 'excursions'
  | 'overview';

interface Service {
  id: string;
  name: string;
  description?: string;
  price?: number;
}

interface ActivityItem {
  id: string;
  name: string;
  description: string;
  badge?: string;
}

type ExcursionType = 'mandatory' | 'included' | 'optional' | 'free';

interface Excursion {
  id: string;
  name: string;
  time: string;
  type: ExcursionType;
}

// Sortable Service Item Component
interface SortableServiceItemProps {
  serviceId: string;
  // service: any;
  service: Service;
  onRemove: () => void;
  isMandatory: boolean;
  isIncluded: boolean;
}

function SortableServiceItem({ serviceId, service, onRemove, isMandatory, isIncluded }: SortableServiceItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: serviceId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const canRemove = !isMandatory && !isIncluded;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 p-2 bg-white rounded border hover:border-gray-300 transition-colors"
    >
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
        <GripVertical className="h-3.5 w-3.5 text-gray-400" />
      </div>
      <div className="flex items-center gap-2 flex-1">
        <Check className="h-3.5 w-3.5 text-green-600" />
        <span className="text-sm font-medium text-gray-900">{service.name}</span>
        {isMandatory && (
          <Badge className="bg-red-100 text-red-700 border-red-300 text-xs px-1.5 py-0">
            Mandatory
          </Badge>
        )}
        {isIncluded && (
          <Badge className="bg-blue-100 text-blue-700 border-blue-300 text-xs px-1.5 py-0">
            Included
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-2">
        {/* {service?.price > 0 && (
          <span className="text-xs text-gray-600">${service.price}</span>
        )} */}
        {service?.price && <span className="text-xs text-gray-600">${service.price}</span>}
        {canRemove && (
          <button
            onClick={onRemove}
            className="p-1 hover:bg-red-50 rounded transition-colors"
          >
            <X className="h-3.5 w-3.5 text-red-600" />
          </button>
        )}
      </div>
    </div>
  );
}

// Sortable Excursion Row Component
interface SortableExcursionCardProps {
  excursion: {
    id: string;
    name: string;
    time: string;
    type: 'mandatory' | 'included' | 'optional' | 'free';
  };
  onRemove: (id: string) => void;
}

function SortableExcursionCard({ excursion, onRemove }: SortableExcursionCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: excursion.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isMandatory = excursion.type === 'mandatory';
  const isIncluded = excursion.type === 'included';
  const canRemove = !isMandatory && !isIncluded;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 p-2 bg-white rounded border border-gray-200 hover:border-gray-300 transition-colors"
    >
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
        <GripVertical className="w-4 h-4 text-gray-400" />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-sm text-gray-900">{excursion.name}</h4>
        <p className="text-xs text-gray-600 mt-0.5">{excursion.time}</p>
      </div>
      <div className="flex items-center gap-2">
        {isMandatory && (
          <Badge className="bg-red-100 text-red-700 border-red-300 text-xs px-1.5 py-0">
            Mandatory
          </Badge>
        )}
        {isIncluded && (
          <Badge className="bg-blue-100 text-blue-700 border-blue-300 text-xs px-1.5 py-0">
            Included
          </Badge>
        )}
        {excursion.type === 'optional' && (
          <Badge className="bg-purple-100 text-purple-700 border-purple-300 text-xs px-1.5 py-0">
            Optional
          </Badge>
        )}
        {excursion.type === 'free' && (
          <Badge className="bg-green-100 text-green-700 border-green-300 text-xs px-1.5 py-0">
            Free
          </Badge>
        )}
        {canRemove && (
          <button
            onClick={() => onRemove(excursion.id)}
            className="p-1 hover:bg-red-50 rounded transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5 text-red-600" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function TourApprovalViewPage({ onNavigate, tourId }: TourApprovalViewPageProps) {
  // const [activeTab, setActiveTab] = useState<'arrival-departure' | 'venue-summary' | 'applicants' | 'excursions' | 'services' | 'overview'>('arrival-departure');
  const [activeTab, setActiveTab] = useState<TabType>('arrival-departure');
  const [approvalStatus, setApprovalStatus] = useState<'pending' | 'approved' | 'rejected'>('pending');

  // Arrival state
  const [arrivalPort, setArrivalPort] = useState('NJF');
  const [arrivalDate, setArrivalDate] = useState('2025-09-18');
  const [arrivalTime, setArrivalTime] = useState('14:30');
  const [arrivalFlightNumber, setArrivalFlightNumber] = useState('IQ 101');
  const [arrivalBookingRef, setArrivalBookingRef] = useState('KUN-ARR-2025');
  const [arrivalAirline, setArrivalAirline] = useState('Iraqi Airways');

  // Departure state
  const [departurePort, setDeparturePort] = useState('NJF');
  const [departureDate, setDepartureDate] = useState('2025-09-27');
  const [departureTime, setDepartureTime] = useState('09:15');
  const [departureFlightNumber, setDepartureFlightNumber] = useState('IQ 202');
  const [departureBookingRef, setDepartureBookingRef] = useState('KUN-DEP-2025');
  const [departureAirline, setDepartureAirline] = useState('Iraqi Airways');

  // Confirmation states
  // const [isArrivalConfirmed, setIsArrivalConfirmed] = useState(false);
  // const [isDepartureConfirmed, setIsDepartureConfirmed] = useState(false);
  const [isArrivalConfirmed] = useState(false);
  const [isDepartureConfirmed] = useState(false);

  // Venue Summary state
  const [tourName, setTourName] = useState('Ashara Mubaraka 1446H');
  const [noOfApplicants, setNoOfApplicants] = useState('25');
  const [faizAccommodation, setFaizAccommodation] = useState<'yes' | 'no'>('yes');
  const [venues, setVenues] = useState([
    {
      id: 'venue-1',
      venueName: 'karbala',
      fromDate: '2025-09-18',
      fromTime: '17:00',
      toDate: '2025-09-23',
      toTime: '10:00'
    },
    {
      id: 'venue-2',
      venueName: 'najaf',
      fromDate: '2025-09-23',
      fromTime: '15:00',
      toDate: '2025-09-27',
      toTime: '06:00'
    }
  ]);

  // Applicants state
  const [selectedApplicants, setSelectedApplicants] = useState<string[]>(['1', '2']);
  const [groupLeader, setGroupLeader] = useState<string | null>('1');
  const [showAddApplicantDialog, setShowAddApplicantDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Family indicators state
  const [familyIndicators, setFamilyIndicators] = useState<Record<string, string | null>>({
    '1': 'F-1',
    '2': 'F-1',
    '3': 'F-2',
  });

  // Services state
  const [applicantServices, setApplicantServices] = useState<Record<string, string[]>>({
    '1': ['mawaid', 'accommodation', 'tour-guide', 'transport'],
    '2': ['mawaid', 'accommodation', 'tour-guide', 'transport'],
  });
  const [draggedService, setDraggedService] = useState<string | null>(null);
  const [dragOverApplicant, setDragOverApplicant] = useState<string | null>(null);

  // Excursions state
  // const [excursionsDay1, setExcursionsDay1] = useState([
  //   { id: 'exc-1-1', name: 'Pehli Ziyarat — Imam Hussain AS', time: 'Morning · Structured first visit', type: 'mandatory' as const },
  //   { id: 'exc-1-2', name: 'Al-Abbas AS shrine', time: 'Evening · First visit · 1.5 hrs', type: 'mandatory' as const }
  // ]);

  // const [excursionsDay2, setExcursionsDay2] = useState([
  //   { id: 'exc-2-1', name: 'Daily ziyarat — Hussain AS', time: 'Morning + evening · Repeats daily', type: 'mandatory' as const },
  //   { id: 'exc-2-2', name: 'Hurr ibn Yazid AS — Maqam', time: 'Afternoon · ~3 hrs · Half day', type: 'included' as const },
  //   { id: 'exc-2-3', name: 'Zainabiya + Khaimgah', time: 'Half day · Always together · 2.5 hrs', type: 'optional' as const },
  //   { id: 'exc-2-4', name: 'Wadi cemetery (Karbala)', time: 'Afternoon · ~2 hrs', type: 'free' as const }
  // ]);

  const [excursionsDay1, setExcursionsDay1] = useState<Excursion[]>([
    {
      id: 'exc-1-1',
      name: 'Pehli Ziyarat — Imam Hussain AS',
      time: 'Morning · Structured first visit',
      type: 'mandatory'
    },
    {
      id: 'exc-1-2',
      name: 'Al-Abbas AS shrine',
      time: 'Evening · First visit · 1.5 hrs',
      type: 'mandatory'
    }
  ]);

  const [excursionsDay2, setExcursionsDay2] = useState<Excursion[]>([
    {
      id: 'exc-2-1',
      name: 'Daily ziyarat — Hussain AS',
      time: 'Morning + evening · Repeats daily',
      type: 'mandatory'
    },
    {
      id: 'exc-2-2',
      name: 'Hurr ibn Yazid AS — Maqam',
      time: 'Afternoon · ~3 hrs · Half day',
      type: 'included'
    },
    {
      id: 'exc-2-3',
      name: 'Zainabiya + Khaimgah',
      time: 'Half day · Always together · 2.5 hrs',
      type: 'optional'
    },
    {
      id: 'exc-2-4',
      name: 'Wadi cemetery (Karbala)',
      time: 'Afternoon · ~2 hrs',
      type: 'free'
    }
  ]);

  const [rejectionReason, setRejectionReason] = useState('');

  // Sidebar state for excursions
  const [sidebarCollapsed, setSidebarCollapsed] = useState({
    transport: false,
    mawaid: true,
    ziyarat: false,
    excursions: true
  });

  const [draggedActivity, setDraggedActivity] = useState<{
    id: string;
    name: string;
    description: string;
    category: string;
  } | null>(null);

  const [dropTargetDay, setDropTargetDay] = useState<number | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleApprove = () => {
    setApprovalStatus('approved');
    toast.success('Tour approved successfully');
  };

  const handleReject = () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    setApprovalStatus('rejected');
    toast.success('Tour rejected');
  };

  const handleSaveChanges = () => {
    toast.success('Changes saved successfully');
  };

  const handleDragStart = (serviceId: string) => {
    setDraggedService(serviceId);
  };

  const handleDragEnd = () => {
    setDraggedService(null);
    setDragOverApplicant(null);
  };

  const handleDragOver = (e: React.DragEvent, applicantId: string) => {
    e.preventDefault();
    setDragOverApplicant(applicantId);
  };

  const handleDragLeave = () => {
    setDragOverApplicant(null);
  };

  const handleDrop = (e: React.DragEvent, applicantId: string) => {
    e.preventDefault();
    if (draggedService) {
      if (!applicantServices[applicantId]?.includes(draggedService)) {
        setApplicantServices(prev => ({
          ...prev,
          [applicantId]: [...(prev[applicantId] || []), draggedService]
        }));
        toast.success('Service assigned successfully');
      } else {
        toast.info('Service already assigned to this applicant');
      }
    }
    setDraggedService(null);
    setDragOverApplicant(null);
  };

  const handleRemoveService = (applicantId: string, serviceId: string) => {
    const service = mockServices.find(s => s.id === serviceId);
    if (service?.type === 'Mandatory' || service?.type === 'Included') {
      toast.error('Cannot remove mandatory or included services');
      return;
    }

    setApplicantServices(prev => ({
      ...prev,
      [applicantId]: prev[applicantId]?.filter(id => id !== serviceId) || []
    }));
    toast.success('Service removed');
  };

  const handleServiceDragEnd = (event: DragEndEvent, applicantId: string) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setApplicantServices(prev => {
        const currentServices = prev[applicantId] || [];
        const oldIndex = currentServices.findIndex((id) => id === active.id);
        const newIndex = currentServices.findIndex((id) => id === over.id);

        return {
          ...prev,
          [applicantId]: arrayMove(currentServices, oldIndex, newIndex)
        };
      });

      toast.success('Service order updated');
    }
  };

  // const handleExcursionDragEnd = (event: DragEndEvent, day: number) => {
  //   const { active, over } = event;

  //   if (over && active.id !== over.id) {
  //     const setState = day === 1 ? setExcursionsDay1 : setExcursionsDay2;
  //     const currentItems = day === 1 ? excursionsDay1 : excursionsDay2;

  //     setState((items:any) => {
  //       const oldIndex = items.findIndex((item) => item.id === active.id);
  //       const newIndex = items.findIndex((item) => item.id === over.id);

  //       return arrayMove(items, oldIndex, newIndex);
  //     });

  //     toast.success('Excursion order updated');
  //   }
  // };

  const handleExcursionDragEnd = (event: DragEndEvent, day: number) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      if (day === 1) {
        setExcursionsDay1((items) => {
          const oldIndex = items.findIndex((item) => item.id === active.id);
          const newIndex = items.findIndex((item) => item.id === over.id);

          return arrayMove(items, oldIndex, newIndex);
        });
      } else {
        setExcursionsDay2((items) => {
          const oldIndex = items.findIndex((item) => item.id === active.id);
          const newIndex = items.findIndex((item) => item.id === over.id);

          return arrayMove(items, oldIndex, newIndex);
        });
      }

      toast.success('Excursion order updated');
    }
  };

  // Activity drag handlers (from sidebar)
  const handleActivityDragStart = (activity: ActivityItem, category: string) => {
    setDraggedActivity({ ...activity, category });
  };

  const handleActivityDragEnd = () => {
    setDraggedActivity(null);
    setDropTargetDay(null);
  };

  const handleDayDragOver = (e: React.DragEvent, day: number) => {
    e.preventDefault();
    setDropTargetDay(day);
  };

  const handleDayDragLeave = () => {
    setDropTargetDay(null);
  };

  const handleDayDrop = (e: React.DragEvent, day: number) => {
    e.preventDefault();
    if (draggedActivity) {
      const newExcursion = {
        id: `${draggedActivity.category}-${Date.now()}`,
        name: draggedActivity.name,
        time: draggedActivity.description,
        type: 'included' as const
      };

      const setState = day === 1 ? setExcursionsDay1 : setExcursionsDay2;
      setState((items) => [...items, newExcursion]);
      toast.success(`${draggedActivity.name} added to Day ${day}`);
    }
    setDraggedActivity(null);
    setDropTargetDay(null);
  };

  const handleRecommendSchedule = () => {
    toast.success('Automated atraaf schedule recommended and applied!');
    // This would typically call an API to get recommended schedule
  };

  const handleToggleApplicant = (applicantId: string) => {
    setSelectedApplicants(
      prev => prev.includes(applicantId)
        ? prev.filter(id => id !== applicantId)
        : [...prev, applicantId]
    );
    if (!selectedApplicants.includes(applicantId)) {
      setShowAddApplicantDialog(false);
      setSearchQuery('');
      toast.success('Applicant added to tour');
    }
  };

  const handleSetGroupLeader = (applicantId: string) => {
    if (selectedApplicants.includes(applicantId)) {
      setGroupLeader(groupLeader === applicantId ? null : applicantId);
    } else {
      toast.error('Please add the applicant to tour first');
    }
  };

  const handleRemoveApplicant = (applicantId: string) => {
    setSelectedApplicants(prev => prev.filter(id => id !== applicantId));
    if (groupLeader === applicantId) {
      setGroupLeader(null);
    }
    setApplicantServices(prev => {
      const newServices = { ...prev };
      delete newServices[applicantId];
      return newServices;
    });
    toast.success('Applicant removed from tour');
  };

  const filteredAvailableApplicants = mockApprovedApplicants.filter(applicant =>
    !selectedApplicants.includes(applicant.id) &&
    (applicant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      applicant.itsNumber.includes(searchQuery))
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b px-6 py-4">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between gap-8">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onNavigate?.('tour-approval')}
              className="h-9 w-9"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="flex items-center gap-2 text-xl font-semibold" style={{ color: '#5B9BD5' }}>
                Tour Approval Details
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                {tourId || 'IRQ-2026-0847'} • Ashara Mubaraka 1446H
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-3">
            <Badge className={`px-4 py-2 ${approvalStatus === 'approved'
              ? 'bg-green-100 text-green-700 border-green-300'
              : approvalStatus === 'rejected'
                ? 'bg-red-100 text-red-700 border-red-300'
                : 'bg-amber-100 text-amber-700 border-amber-300'
              }`}>
              {approvalStatus === 'approved' ? '✓ Approved' : approvalStatus === 'rejected' ? '✗ Rejected' : 'Pending Approval'}
            </Badge>
            <Button onClick={handleSaveChanges} style={{ background: '#5B9BD5' }} className="text-white">
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
            {approvalStatus === 'pending' && (
              <>
                <Button
                  onClick={handleReject}
                  variant="outline"
                  className="text-red-600 hover:bg-red-50 border-red-300"
                >
                  <X className="h-4 w-4 mr-2" />
                  Reject
                </Button>
                <Button
                  onClick={handleApprove}
                  style={{ background: '#10B981' }}
                  className="text-white"
                >
                  <Check className="h-4 w-4 mr-2" />
                  Approve Tour
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 pt-6">
        {/* Tabs */}
        <div className="flex flex-wrap items-center gap-0 mb-6 border-b border-border">
          {[
            { id: 'arrival-departure', label: 'Arrival & Departure', icon: Plane },
            { id: 'venue-summary', label: 'Venue Summary', icon: FileText },
            { id: 'applicants', label: 'Applicants', icon: Users },
            { id: 'services', label: 'Services', icon: Settings },
            { id: 'excursions', label: 'Excursions', icon: MapPin },
            { id: 'overview', label: 'Overview', icon: FileText }
          ].map(({ id, label, icon: Icon }, index) => (
            <button
              key={id}
              onClick={() => setActiveTab(id as TabType)}
              className={`flex items-center gap-2 px-5 py-3 relative text-sm ${activeTab === id
                ? 'text-primary'
                : 'text-muted-foreground hover:text-foreground'
                }`}
            >
              <div className={`w-7 h-7 rounded-full flex items-center justify-center font-semibold text-xs ${activeTab === id
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground'
                }`}>
                {index + 1}
              </div>
              <span className="font-medium">{label}</span>
              {activeTab === id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="pb-8">
          {/* Tab 1: Arrival & Departure */}
          {activeTab === 'arrival-departure' && (
            <div className="space-y-5">
              {/* Arrival and Departure Cards Side by Side */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Arrival Card */}
                <Card className="border-2 border-primary/40 shadow-sm bg-white">
                  <CardHeader className="pb-3 bg-card">
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center">
                        <ArrowRight className="w-5 h-5 text-primary-foreground" />
                      </div>
                      <CardTitle className="text-base font-semibold text-foreground">Arrival Details</CardTitle>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4 pt-4 bg-card">
                    {/* Row 1: Flight No. and Time */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-semibold mb-1.5 block text-muted-foreground uppercase">Flight No.<span className="text-red-500">*</span></Label>
                        <Input
                          placeholder="IQ 101"
                          className="h-9 text-xs"
                          value={arrivalFlightNumber}
                          onChange={(e) => setArrivalFlightNumber(e.target.value)}
                          disabled={isArrivalConfirmed}
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold mb-1.5 block text-muted-foreground uppercase">Time <span className="text-red-500">*</span></Label>
                        <Input
                          type="time"
                          className="h-9 text-xs"
                          value={arrivalTime}
                          onChange={(e) => setArrivalTime(e.target.value)}
                          disabled={isArrivalConfirmed}
                        />
                      </div>
                    </div>

                    {/* Row 2: Date and Port */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-semibold mb-1.5 block text-muted-foreground uppercase">Date <span className="text-red-500">*</span></Label>
                        <Input
                          type="date"
                          className="h-9 text-xs"
                          value={arrivalDate}
                          onChange={(e) => setArrivalDate(e.target.value)}
                          disabled={isArrivalConfirmed}
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold mb-1.5 block text-muted-foreground uppercase">Port <span className="text-red-500">*</span></Label>
                        <Select value={arrivalPort} onValueChange={setArrivalPort} disabled={isArrivalConfirmed}>
                          <SelectTrigger className="h-9 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {portMasterData.map(port => (
                              <SelectItem key={port.id} value={port.id}>{port.name} ({port.code})</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Row 3: Booking Ref and Airline */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-semibold mb-1.5 block text-muted-foreground uppercase">Booking Ref</Label>
                        <Input
                          placeholder="Booking reference"
                          className="h-9 text-xs"
                          value={arrivalBookingRef}
                          onChange={(e) => setArrivalBookingRef(e.target.value)}
                          disabled={isArrivalConfirmed}
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold mb-1.5 block text-muted-foreground uppercase">Airline</Label>
                        <Input
                          placeholder="Airline name"
                          className="h-9 text-xs"
                          value={arrivalAirline}
                          onChange={(e) => setArrivalAirline(e.target.value)}
                          disabled={isArrivalConfirmed}
                        />
                      </div>
                    </div>

                    {/* Row 4: Confirmed? */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="pb-0.5">
                        <Label className="text-xs font-semibold mb-1.5 block text-muted-foreground uppercase">Confirmed?</Label>
                        <div className="flex items-center h-9">
                          <Badge className={isArrivalConfirmed ? "bg-green-100 text-green-700 border-green-300" : "bg-gray-100 text-gray-600 border-gray-300"}>
                            {isArrivalConfirmed ? '✓ Yes' : 'No'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Departure Card */}
                <Card className="border-2 border-primary/40 shadow-sm bg-white">
                  <CardHeader className="pb-3 bg-card">
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center">
                        <Plane className="w-5 h-5 text-primary-foreground" />
                      </div>
                      <CardTitle className="text-base font-semibold text-foreground">Departure Details</CardTitle>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4 pt-4 bg-card">
                    {/* Row 1: Flight No. and Time */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-semibold mb-1.5 block text-muted-foreground uppercase">Flight No.<span className="text-red-500">*</span></Label>
                        <Input
                          placeholder="IQ 202"
                          className="h-9 text-xs"
                          value={departureFlightNumber}
                          onChange={(e) => setDepartureFlightNumber(e.target.value)}
                          disabled={isDepartureConfirmed}
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold mb-1.5 block text-muted-foreground uppercase">Time <span className="text-red-500">*</span></Label>
                        <Input
                          type="time"
                          className="h-9 text-xs"
                          value={departureTime}
                          onChange={(e) => setDepartureTime(e.target.value)}
                          disabled={isDepartureConfirmed}
                        />
                      </div>
                    </div>

                    {/* Row 2: Date and Port */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-semibold mb-1.5 block text-muted-foreground uppercase">Date <span className="text-red-500">*</span></Label>
                        <Input
                          type="date"
                          className="h-9 text-xs"
                          value={departureDate}
                          onChange={(e) => setDepartureDate(e.target.value)}
                          disabled={isDepartureConfirmed}
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold mb-1.5 block text-muted-foreground uppercase">Port <span className="text-red-500">*</span></Label>
                        <Select value={departurePort} onValueChange={setDeparturePort} disabled={isDepartureConfirmed}>
                          <SelectTrigger className="h-9 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {portMasterData.map(port => (
                              <SelectItem key={port.id} value={port.id}>{port.name} ({port.code})</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Row 3: Booking Ref and Airline */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-semibold mb-1.5 block text-muted-foreground uppercase">Booking Ref</Label>
                        <Input
                          placeholder="Booking reference"
                          className="h-9 text-xs"
                          value={departureBookingRef}
                          onChange={(e) => setDepartureBookingRef(e.target.value)}
                          disabled={isDepartureConfirmed}
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold mb-1.5 block text-muted-foreground uppercase">Airline</Label>
                        <Input
                          placeholder="Airline name"
                          className="h-9 text-xs"
                          value={departureAirline}
                          onChange={(e) => setDepartureAirline(e.target.value)}
                          disabled={isDepartureConfirmed}
                        />
                      </div>
                    </div>

                    {/* Row 4: Confirmed? */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="pb-0.5">
                        <Label className="text-xs font-semibold mb-1.5 block text-muted-foreground uppercase">Confirmed?</Label>
                        <div className="flex items-center h-9">
                          <Badge className={isDepartureConfirmed ? "bg-green-100 text-green-700 border-green-300" : "bg-gray-100 text-gray-600 border-gray-300"}>
                            {isDepartureConfirmed ? '✓ Yes' : 'No'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Tab 2: Venue Summary */}
          {activeTab === 'venue-summary' && (
            <div className="space-y-5">
              <Card className='bg-white'>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Venue Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  {/* Tour Information */}
                  <div className="grid grid-cols-6 lg:grid-cols-12 gap-4">
                    <div className="col-span-6">
                      <Label className="text-xs font-semibold mb-1.5 block text-muted-foreground uppercase">
                        Tour Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        placeholder="Enter tour name"
                        className="h-9 text-xs"
                        value={tourName}
                        onChange={(e) => setTourName(e.target.value)}
                      />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs font-semibold mb-1.5 block text-muted-foreground uppercase">
                        No. of Applicants <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        type="number"
                        placeholder="0"
                        className="h-9 text-xs"
                        value={noOfApplicants}
                        onChange={(e) => setNoOfApplicants(e.target.value)}
                      />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs font-semibold mb-1.5 block text-muted-foreground uppercase">
                        Faiz Accommodation <span className="text-red-500">*</span>
                      </Label>
                      <RadioGroup
                        value={faizAccommodation}
                        onValueChange={(value: 'yes' | 'no') => setFaizAccommodation(value)}
                        className="flex gap-4 h-9 items-center"
                      >
                        <div className="flex items-center space-x-1.5">
                          <RadioGroupItem value="yes" id="faiz-yes" className="h-4 w-4" />
                          <Label htmlFor="faiz-yes" className="font-normal cursor-pointer text-xs">Yes</Label>
                        </div>
                        <div className="flex items-center space-x-1.5">
                          <RadioGroupItem value="no" id="faiz-no" className="h-4 w-4" />
                          <Label htmlFor="faiz-no" className="font-normal cursor-pointer text-xs">No</Label>
                        </div>
                      </RadioGroup>
                    </div>
                  </div>

                  {/* Venue Schedule */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-xs font-semibold block text-muted-foreground uppercase">
                          Venue Schedule <span className="text-red-500">*</span>
                        </Label>
                        <p className="text-[10px] text-muted-foreground mt-0.5">Add venues with check-in and check-out details</p>
                      </div>
                      <Button
                        type="button"
                        onClick={() => {
                          const newVenue = {
                            id: `venue-${Date.now()}`,
                            venueName: '',
                            fromDate: '',
                            fromTime: '',
                            toDate: '',
                            toTime: ''
                          };
                          setVenues([...venues, newVenue]);
                        }}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground text-xs h-8"
                        size="sm"
                      >
                        <Plus className="w-3.5 h-3.5 mr-1.5" />
                        Add Venue
                      </Button>
                    </div>

                    {/* Venue List */}
                    {venues.length === 0 ? (
                      <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                        <MapPin className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                        <p className="text-muted-foreground text-xs">No venues added yet</p>
                        <p className="text-muted-foreground text-[10px] mt-1">Click "Add Venue" to start building your itinerary</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {venues.map((venue, index) => (
                          <Card key={venue.id} className="border border-border">
                            <CardContent className="p-3">
                              <div className="flex items-start gap-3">
                                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold text-xs shrink-0">
                                  {index + 1}
                                </div>

                                <div className="flex-1 space-y-3">
                                  {/* Venue Name Dropdown */}
                                  <div>
                                    <Label className="text-[10px] font-semibold mb-1 block text-muted-foreground uppercase">
                                      Venue Name <span className="text-red-500">*</span>
                                    </Label>
                                    <Select
                                      value={venue.venueName}
                                      onValueChange={(value) => {
                                        const updatedVenues = [...venues];
                                        updatedVenues[index].venueName = value;
                                        setVenues(updatedVenues);
                                      }}
                                    >
                                      <SelectTrigger className="h-8 text-xs">
                                        <SelectValue placeholder="Select venue..." />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {venueMasterData.map(v => (
                                          <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>

                                  {/* From & To DateTime */}
                                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                    {/* Check-in (From) */}
                                    <div className="space-y-2">
                                      <Label className="text-[10px] font-semibold block text-muted-foreground uppercase">
                                        Check-in <span className="text-red-500">*</span>
                                      </Label>
                                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                                        <Input
                                          type="date"
                                          className="h-7 text-xs"
                                          value={venue.fromDate}
                                          onChange={(e) => {
                                            const updatedVenues = [...venues];
                                            updatedVenues[index].fromDate = e.target.value;
                                            setVenues(updatedVenues);
                                          }}
                                        />
                                        <Input
                                          type="time"
                                          className="h-7 text-xs"
                                          value={venue.fromTime}
                                          onChange={(e) => {
                                            const updatedVenues = [...venues];
                                            updatedVenues[index].fromTime = e.target.value;
                                            setVenues(updatedVenues);
                                          }}
                                        />
                                      </div>
                                    </div>

                                    {/* Check-out (To) */}
                                    <div className="space-y-2">
                                      <Label className="text-[10px] font-semibold block text-muted-foreground uppercase">
                                        Check-out <span className="text-red-500">*</span>
                                      </Label>
                                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                                        <Input
                                          type="date"
                                          className="h-7 text-xs"
                                          value={venue.toDate}
                                          onChange={(e) => {
                                            const updatedVenues = [...venues];
                                            updatedVenues[index].toDate = e.target.value;
                                            setVenues(updatedVenues);
                                          }}
                                        />
                                        <Input
                                          type="time"
                                          className="h-7 text-xs"
                                          value={venue.toTime}
                                          onChange={(e) => {
                                            const updatedVenues = [...venues];
                                            updatedVenues[index].toTime = e.target.value;
                                            setVenues(updatedVenues);
                                          }}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Delete Button */}
                                {venues.length > 1 && (
                                  <button
                                    onClick={() => {
                                      setVenues(venues.filter((_, i) => i !== index));
                                    }}
                                    className="p-1.5 hover:bg-red-50 rounded transition-colors text-red-600"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Tab 3: Applicants */}
          {activeTab === 'applicants' && (
            <div className="space-y-4">
              <Card className="border-purple-200">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-purple-600" />
                      <h3 className="text-sm font-semibold text-gray-900">Selected Applicants</h3>
                      <Badge className="bg-purple-100 text-purple-700 border-purple-300 text-xs">
                        {selectedApplicants.length} Applicants
                      </Badge>
                    </div>
                    <Button
                      onClick={() => setShowAddApplicantDialog(true)}
                      style={{ background: '#5B9BD5' }}
                      className="text-white h-9"
                      size="sm"
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      Add Applicants
                    </Button>
                  </div>

                  {/* Selected Applicants List */}
                  {selectedApplicants.length === 0 ? (
                    <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed">
                      <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p className="text-sm font-medium">No applicants added to tour yet</p>
                      <p className="text-xs mt-1">Click "Add Applicants" button to start</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {selectedApplicants.map((applicantId) => {
                        const applicant = mockApprovedApplicants.find(a => a.id === applicantId);
                        if (!applicant) return null;

                        return (
                          <div key={applicantId} className="flex items-center justify-between p-3 bg-white border rounded-lg hover:bg-gray-50 transition-colors gap-2">
                            <div className="flex items-center gap-3 flex-1">
                              <img
                                src={applicant.photo}
                                alt={applicant.name}
                                className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                              />
                              <div className="flex-1 gap-2">
                                <div className="flex justify-center items-center gap-2">
                                  <h4 className="text-sm font-semibold text-gray-900">{applicant.name}</h4>
                                  {groupLeader === applicantId && (
                                    <Badge className="bg-yellow-100 text-yellow-700 border-yellow-300 text-xs px-1.5 py-0">
                                      <Star className="h-3 w-3 mr-0.5 fill-yellow-700" />
                                      Leader
                                    </Badge>
                                  )}
                                  <Select
                                    value={familyIndicators[applicantId] || ''}
                                    onValueChange={(value) => {
                                      setFamilyIndicators(prev => ({
                                        ...prev,
                                        [applicantId]: value === 'none' ? null : value
                                      }));
                                    }}
                                  >
                                    <SelectTrigger className="h-6 w-16 text-xs bg-blue-100 text-blue-700 border-blue-300 px-1.5">
                                      <SelectValue placeholder="F-?" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="none">None</SelectItem>
                                      {Array.from({ length: 13 }, (_, i) => (
                                        <SelectItem key={i + 1} value={`F-${i + 1}`}>
                                          F-{i + 1}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-600">
                                  <span>ITS: {applicant.itsNumber}</span>
                                  <span className="text-gray-300">•</span>
                                  <span>{applicant.age} years</span>
                                  <span className="text-gray-300">•</span>
                                  <Badge className="bg-gray-100 text-gray-700 border-gray-300 text-xs px-1.5 py-0">
                                    {applicant.applicantType}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                            <div className="flex justify-center items-center gap-2">
                              <button
                                onClick={() => handleSetGroupLeader(applicantId)}
                                className={`p-2 rounded transition-colors ${groupLeader === applicantId
                                  ? 'bg-yellow-100 text-yellow-600'
                                  : 'hover:bg-gray-100 text-gray-400'
                                  }`}
                                title={groupLeader === applicantId ? "Remove as Group Leader" : "Set as Group Leader"}
                              >
                                <Star className={`h-4 w-4 ${groupLeader === applicantId ? 'fill-yellow-600' : ''}`} />
                              </button>
                              <button
                                onClick={() => handleRemoveApplicant(applicantId)}
                                className="p-2 hover:bg-red-50 rounded transition-colors text-red-600"
                                title="Remove from tour"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Add Applicant Dialog */}
              {showAddApplicantDialog && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAddApplicantDialog(false)}>
                  <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[600px] flex flex-col" onClick={(e) => e.stopPropagation()}>
                    <div className="p-5 border-b flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Add Applicants to Tour</h3>
                        <p className="text-sm text-gray-600 mt-0.5">Search and add approved visa applicants</p>
                      </div>
                      <button
                        onClick={() => {
                          setShowAddApplicantDialog(false);
                          setSearchQuery('');
                        }}
                        className="p-2 hover:bg-gray-100 rounded transition-colors"
                      >
                        <X className="h-5 w-5 text-gray-500" />
                      </button>
                    </div>

                    <div className="p-5 border-b">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          placeholder="Search by name or ITS number..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-10 h-10"
                        />
                      </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-5">
                      {filteredAvailableApplicants.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                          <Search className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                          <p className="text-sm">
                            {searchQuery ? 'No applicants found matching your search' : 'All approved applicants have been added to the tour'}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {filteredAvailableApplicants.map((applicant) => (
                            <div
                              key={applicant.id}
                              className="flex items-center justify-between p-3 bg-gray-50 border rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors cursor-pointer"
                              onClick={() => handleToggleApplicant(applicant.id)}
                            >
                              <div className="flex items-center gap-3 flex-1">
                                <img
                                  src={applicant.photo}
                                  alt={applicant.name}
                                  className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                                />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <h4 className="text-sm font-semibold text-gray-900">{applicant.name}</h4>
                                    {applicant.familyIndicator && (
                                      <Badge className="bg-blue-100 text-blue-700 border-blue-300 text-xs px-1.5 py-0">
                                        {applicant.familyIndicator}
                                      </Badge>
                                    )}
                                    {applicant.needsAccommodation && (
                                      <Badge className="bg-purple-100 text-purple-700 border-purple-300 text-xs px-1.5 py-0">
                                        🏨 Accommodation
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-600">
                                    <span>ITS: {applicant.itsNumber}</span>
                                    <span className="text-gray-300">•</span>
                                    <span>{applicant.age} years</span>
                                    <span className="text-gray-300">•</span>
                                    <Badge className="bg-gray-100 text-gray-700 border-gray-300 text-xs px-1.5 py-0">
                                      {applicant.applicantType}
                                    </Badge>
                                  </div>
                                </div>
                              </div>
                              <Button size="sm" style={{ background: '#5B9BD5' }} className="text-white h-8">
                                <Plus className="h-4 w-4 mr-1" />
                                Add
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tab 4: Services */}
          {activeTab === 'services' && (
            <div className="space-y-4">
              {selectedApplicants.length === 0 ? (
                <Card className="border-amber-200">
                  <CardContent className="p-6">
                    <div className="text-center py-8 text-gray-500">
                      <Settings className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p>No applicants added to tour yet</p>
                      <p className="text-sm mt-2">Please add applicants from the Applicants tab first</p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
                  {/* Left Sidebar - Available Services */}
                  <div className="space-y-3">
                    <Card className="border-blue-200 sticky top-4 bg-white">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                          <Settings className="h-4 w-4 text-blue-600" />
                          Available Services
                        </CardTitle>
                        <p className="text-xs text-gray-600 mt-1">Drag services to applicants</p>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        {mockServices.map((service) => (
                          <div
                            key={service.id}
                            draggable
                            onDragStart={() => handleDragStart(service.id)}
                            onDragEnd={handleDragEnd}
                            className={`p-2.5 rounded-lg border-2 cursor-move transition-all ${draggedService === service.id
                              ? 'border-blue-500 bg-blue-50 opacity-50'
                              : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/50'
                              }`}
                          >
                            <div className="flex items-start gap-2">
                              <GripVertical className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm text-gray-900 mb-1">{service.name}</div>
                                <div className="flex items-center gap-1.5">
                                  {service.type === 'Mandatory' && (
                                    <Badge className="bg-red-100 text-red-700 border-red-300 text-xs px-1.5 py-0">
                                      Mandatory
                                    </Badge>
                                  )}
                                  {service.type === 'Included' && (
                                    <Badge className="bg-blue-100 text-blue-700 border-blue-300 text-xs px-1.5 py-0">
                                      Included
                                    </Badge>
                                  )}
                                  {service.type === 'Optional' && (
                                    <Badge className="bg-gray-100 text-gray-700 border-gray-300 text-xs px-1.5 py-0">
                                      Optional
                                    </Badge>
                                  )}
                                </div>
                                {service.price > 0 && (
                                  <div className="text-sm text-gray-900 font-medium mt-1">${service.price}</div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Right Side - Applicants List */}
                  <div className="space-y-3">
                    {selectedApplicants.map((applicantId) => {
                      const applicant = mockApprovedApplicants.find(a => a.id === applicantId);
                      if (!applicant) return null;

                      const assignedServices = applicantServices[applicantId] || [];
                      const isDragOver = dragOverApplicant === applicantId;

                      return (
                        <div
                          key={applicantId}
                          onDragOver={(e) => handleDragOver(e, applicantId)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, applicantId)}
                          className={`transition-all ${isDragOver ? 'scale-[1.02]' : ''
                            }`}
                        >
                          <Card className={`bg-white border-2 transition-all ${isDragOver
                            ? 'border-blue-500 bg-blue-50/50 shadow-lg'
                            : 'border-gray-200'
                            }`}>
                            <CardContent className="p-4">
                              {/* Applicant Header */}
                              <div className="flex items-start justify-between mb-3 pb-3 border-b">
                                <div className="flex items-start gap-3">
                                  <img
                                    src={applicant.photo}
                                    alt={applicant.name}
                                    className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                                  />
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <h3 className="text-sm font-semibold text-gray-900">{applicant.name}</h3>
                                      {familyIndicators[applicantId] && (
                                        <Badge className="bg-blue-100 text-blue-700 border-blue-300 text-xs px-1.5 py-0">
                                          {familyIndicators[applicantId]}
                                        </Badge>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2 mt-0.5">
                                      <p className="text-xs text-gray-600">ITS: {applicant.itsNumber}</p>
                                      <span className="text-gray-300">•</span>
                                      <p className="text-xs text-gray-600">{applicant.age} years</p>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Assigned Services */}
                              <div>
                                <h4 className="text-xs font-medium text-gray-700 mb-2">Assigned Services</h4>
                                {assignedServices.length === 0 ? (
                                  <div className={`p-4 rounded-lg border-2 border-dashed text-center transition-all ${isDragOver
                                    ? 'border-blue-400 bg-blue-50'
                                    : 'border-gray-200 bg-gray-50'
                                    }`}>
                                    <p className="text-xs text-gray-500">
                                      {isDragOver ? 'Drop service here' : 'Drag services here from the sidebar'}
                                    </p>
                                  </div>
                                ) : (
                                  <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={(event) => handleServiceDragEnd(event, applicantId)}
                                  >
                                    <SortableContext items={assignedServices} strategy={verticalListSortingStrategy}>
                                      <div className={`space-y-1.5 p-2 rounded-lg ${isDragOver ? 'bg-blue-50 border-2 border-dashed border-blue-400' : ''
                                        }`}>
                                        {assignedServices.map((serviceId) => {
                                          const service = mockServices.find(s => s.id === serviceId);
                                          if (!service) return null;

                                          const isMandatory = service.type === 'Mandatory';
                                          const isIncluded = service.type === 'Included';

                                          return (
                                            <SortableServiceItem
                                              key={serviceId}
                                              serviceId={serviceId}
                                              service={service}
                                              onRemove={() => handleRemoveService(applicantId, serviceId)}
                                              isMandatory={isMandatory}
                                              isIncluded={isIncluded}
                                            />
                                          );
                                        })}
                                      </div>
                                    </SortableContext>
                                  </DndContext>
                                )}
                              </div>

                              {/* Summary */}
                              {assignedServices.length > 0 && (
                                <div className="mt-3 pt-3 border-t flex justify-between text-sm">
                                  <span className="text-gray-600">Total Cost:</span>
                                  <span className="font-semibold text-gray-900">
                                    ${assignedServices.reduce((total, serviceId) => {
                                      const service = mockServices.find(s => s.id === serviceId);
                                      return total + (service?.price || 0);
                                    }, 0)}
                                  </span>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tab 5: Excursions */}
          {activeTab === 'excursions' && (
            <div className="flex flex-wrap gap-4">
              {/* Main Content Area */}
              <div className="flex-1 space-y-4">
                {/* Header with Recommend Button */}
                <div className="mb-6 flex items-center justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Select excursions</h2>
                    <p className="text-xs text-muted-foreground mt-1">
                      REF: {tourId || 'IRQ-2026-0847'} · {selectedApplicants.length} pax · Pending Approval · Drag to reorder
                    </p>
                  </div>
                  <Button
                    onClick={handleRecommendSchedule}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Recommend Atraaf Schedule
                  </Button>

                </div>

                {/* Info Banner */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-2">
                    <GripVertical className="w-4 h-4 text-green-700 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-green-900">
                      Booking approved. Select and reorder excursions for each day by dragging. Mandatory ziyarats are pre-selected. Submit at least 48 hrs before travel.
                    </p>
                  </div>
                </div>

                {/* Arrival Day */}
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-sm text-gray-900">Arrival day — 12 Mar</h3>
                      <p className="text-xs text-gray-600 mt-0.5">Coach transfer Najaf → Karbala</p>
                    </div>
                    <Badge className="bg-blue-100 text-blue-700 border-blue-300 text-xs">Transport</Badge>
                  </div>
                  <p className="text-xs text-gray-600 italic mt-3">No excursions on arrival day. Transport included.</p>
                </div>

                {/* Karbala - Day 1 */}
                <div
                  className={`bg-amber-50 rounded-lg p-4 mb-4 transition-all ${dropTargetDay === 1 ? 'ring-2 ring-blue-500 ring-offset-2' : ''
                    }`}
                  onDragOver={(e) => handleDayDragOver(e, 1)}
                  onDragLeave={handleDayDragLeave}
                  onDrop={(e) => handleDayDrop(e, 1)}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-sm text-gray-900">Karbala — Day 1 · 13 Mar</h3>
                      <p className="text-xs text-gray-600 mt-0.5">Pehli Ziyarat day · Drag to reorder</p>
                    </div>
                    <span className="text-xs font-semibold text-green-700">{excursionsDay1.length} selected</span>
                  </div>

                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(event) => handleExcursionDragEnd(event, 1)}
                  >
                    <SortableContext items={excursionsDay1.map(e => e.id)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-2">
                        {excursionsDay1.map((excursion) => (
                          <SortableExcursionCard
                            key={excursion.id}
                            excursion={excursion}
                            onRemove={(id) => {
                              setExcursionsDay1(excursionsDay1.filter(e => e.id !== id));
                              toast.success('Excursion removed');
                            }}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>

                {/* Karbala - Day 2 */}
                <div
                  className={`bg-amber-50 rounded-lg p-4 mb-4 transition-all ${dropTargetDay === 2 ? 'ring-2 ring-blue-500 ring-offset-2' : ''
                    }`}
                  onDragOver={(e) => handleDayDragOver(e, 2)}
                  onDragLeave={handleDayDragLeave}
                  onDrop={(e) => handleDayDrop(e, 2)}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-sm text-gray-900">Karbala — Day 2 · 14 Mar</h3>
                      <p className="text-xs text-gray-600 mt-0.5">Drag to reorder excursions</p>
                    </div>
                    <span className="text-xs font-semibold text-green-700">{excursionsDay2.length} selected</span>
                  </div>

                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(event) => handleExcursionDragEnd(event, 2)}
                  >
                    <SortableContext items={excursionsDay2.map(e => e.id)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-2">
                        {excursionsDay2.map((excursion) => (
                          <SortableExcursionCard
                            key={excursion.id}
                            excursion={excursion}
                            onRemove={(id) => {
                              setExcursionsDay2(excursionsDay2.filter(e => e.id !== id));
                              toast.success('Excursion removed');
                            }}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>
              </div>

              {/* Right Sidebar - Available Atraafs & Activities */}
              <div className="lg:w-80 w-full shrink-0">
                <div className="sticky top-4">
                  <div className="bg-white border rounded-lg shadow-sm">
                    <div className="p-4 border-b">
                      <h3 className="font-semibold text-sm text-gray-900">Available Atraafs & Activities</h3>
                      <p className="text-xs text-gray-600 mt-1">Drag items onto a day</p>
                    </div>

                    <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
                      {/* Transport Section */}
                      <div className="border-b">
                        <button
                          onClick={() => setSidebarCollapsed(prev => ({ ...prev, transport: !prev.transport }))}
                          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <Truck className="w-4 h-4 text-blue-600" />
                            <span className="font-medium text-sm">Transport</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="text-xs">{availableTransport.length}</Badge>
                            <ArrowRight className={`w-4 h-4 transition-transform ${sidebarCollapsed.transport ? '' : 'rotate-90'}`} />
                          </div>
                        </button>
                        {!sidebarCollapsed.transport && (
                          <div className="px-2 pb-2 space-y-1">
                            {availableTransport.map((item) => (
                              <div
                                key={item.id}
                                draggable
                                onDragStart={() => handleActivityDragStart(item, 'transport')}
                                onDragEnd={handleActivityDragEnd}
                                className="bg-white border rounded p-2.5 cursor-move hover:border-blue-500 hover:bg-blue-50 transition-all group"
                              >
                                <div className="flex items-start gap-2">
                                  <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0"></div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2">
                                      <h4 className="font-medium text-sm text-gray-900">{item.name}</h4>
                                      {item.badge && (
                                        <Badge className="bg-blue-100 text-blue-700 text-xs">{item.badge}</Badge>
                                      )}
                                    </div>
                                    <p className="text-xs text-gray-600 mt-0.5">{item.description}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                            <p className="text-xs text-gray-500 text-center py-2">↑ drag onto a day</p>
                          </div>
                        )}
                      </div>

                      {/* Mawaid Section */}
                      <div className="border-b">
                        <button
                          onClick={() => setSidebarCollapsed(prev => ({ ...prev, mawaid: !prev.mawaid }))}
                          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-lg">🍽️</span>
                            <span className="font-medium text-sm">Mawaid (Meals)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="text-xs">{availableMawaid.length}</Badge>
                            <ArrowRight className={`w-4 h-4 transition-transform ${sidebarCollapsed.mawaid ? '' : 'rotate-90'}`} />
                          </div>
                        </button>
                        {!sidebarCollapsed.mawaid && (
                          <div className="px-2 pb-2 space-y-1">
                            {availableMawaid.map((item) => (
                              <div
                                key={item.id}
                                draggable
                                onDragStart={() => handleActivityDragStart(item, 'mawaid')}
                                onDragEnd={handleActivityDragEnd}
                                className="bg-white border rounded p-2.5 cursor-move hover:border-orange-500 hover:bg-orange-50 transition-all group"
                              >
                                <div className="flex items-start gap-2">
                                  <div className="w-2 h-2 rounded-full bg-orange-500 mt-1.5 flex-shrink-0"></div>
                                  <div className="flex-1 min-w-0">
                                    <h4 className="font-medium text-sm text-gray-900">{item.name}</h4>
                                    <p className="text-xs text-gray-600 mt-0.5">{item.description}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                            <p className="text-xs text-gray-500 text-center py-2">↑ drag onto a day</p>
                          </div>
                        )}
                      </div>

                      {/* Ziyarat Visits Section */}
                      <div className="border-b">
                        <button
                          onClick={() => setSidebarCollapsed(prev => ({ ...prev, ziyarat: !prev.ziyarat }))}
                          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-purple-600" />
                            <span className="font-medium text-sm">Ziyarat Visits</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="text-xs">{availableZiyarat.length}</Badge>
                            <ArrowRight className={`w-4 h-4 transition-transform ${sidebarCollapsed.ziyarat ? '' : 'rotate-90'}`} />
                          </div>
                        </button>
                        {!sidebarCollapsed.ziyarat && (
                          <div className="px-2 pb-2 space-y-1">
                            {availableZiyarat.map((item) => (
                              <div
                                key={item.id}
                                draggable
                                onDragStart={() => handleActivityDragStart(item, 'ziyarat')}
                                onDragEnd={handleActivityDragEnd}
                                className="bg-white border rounded p-2.5 cursor-move hover:border-purple-500 hover:bg-purple-50 transition-all group"
                              >
                                <div className="flex items-start gap-2">
                                  <div className="w-2 h-2 rounded-full bg-purple-500 mt-1.5 flex-shrink-0"></div>
                                  <div className="flex-1 min-w-0">
                                    <h4 className="font-medium text-sm text-gray-900">{item.name}</h4>
                                    <p className="text-xs text-gray-600 mt-0.5">{item.description}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                            <p className="text-xs text-gray-500 text-center py-2">↑ drag onto a day</p>
                          </div>
                        )}
                      </div>

                      {/* Excursions Section */}
                      <div>
                        <button
                          onClick={() => setSidebarCollapsed(prev => ({ ...prev, excursions: !prev.excursions }))}
                          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-lg">🎯</span>
                            <span className="font-medium text-sm">Excursions</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="text-xs">{availableExcursions.length}</Badge>
                            <ArrowRight className={`w-4 h-4 transition-transform ${sidebarCollapsed.excursions ? '' : 'rotate-90'}`} />
                          </div>
                        </button>
                        {!sidebarCollapsed.excursions && (
                          <div className="px-2 pb-2 space-y-1">
                            {availableExcursions.map((item) => (
                              <div
                                key={item.id}
                                draggable
                                onDragStart={() => handleActivityDragStart(item, 'excursions')}
                                onDragEnd={handleActivityDragEnd}
                                className="bg-white border rounded p-2 cursor-move hover:border-green-500 hover:bg-green-50 transition-all group flex items-center gap-2"
                              >
                                <GripVertical className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-medium text-sm text-gray-900">{item.name}</h4>
                                  <p className="text-xs text-gray-600 mt-0.5">{item.description}</p>
                                </div>
                              </div>
                            ))}
                            <p className="text-xs text-gray-500 text-center py-2">↑ drag onto a day</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tab 6: Overview */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Tour Summary */}
              <Card className="border-blue-200 bg-white">
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <FileText className="h-4 w-4 text-blue-600" />
                    Tour Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-gray-600">Tour Name</Label>
                      <p className="text-sm font-medium text-gray-900">{tourName}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">Total Applicants</Label>
                      <p className="text-sm font-medium text-gray-900">{selectedApplicants.length}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">Faiz Accommodation</Label>
                      <p className="text-sm font-medium text-gray-900">{faizAccommodation === 'yes' ? 'Yes' : 'No'}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">Total Venues</Label>
                      <p className="text-sm font-medium text-gray-900">{venues.length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Travel Details */}
              <Card className="border-green-200 bg-white">
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Plane className="h-4 w-4 text-green-600" />
                    Travel Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-gray-600">Arrival</Label>
                      <p className="text-sm font-medium text-gray-900">
                        {portMasterData.find(p => p.id === arrivalPort)?.name || arrivalPort}
                      </p>
                      <p className="text-xs text-gray-600">{arrivalFlightNumber} • {arrivalDate} at {arrivalTime}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">Departure</Label>
                      <p className="text-sm font-medium text-gray-900">
                        {portMasterData.find(p => p.id === departurePort)?.name || departurePort}
                      </p>
                      <p className="text-xs text-gray-600">{departureFlightNumber} • {departureDate} at {departureTime}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Statistics */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card className="border-orange-200 bg-white">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                        <Calendar className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-900">{excursionsDay1.length + excursionsDay2.length}</p>
                        <p className="text-xs text-gray-600">Excursions</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-pink-200 bg-white">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center">
                        <Settings className="h-5 w-5 text-pink-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-900">{mockServices.length}</p>
                        <p className="text-xs text-gray-600">Services Available</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-purple-200 bg-white">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <MapPin className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-900">{venues.length}</p>
                        <p className="text-xs text-gray-600">Venues</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </div>

        {/* Rejection Reason */}
        {approvalStatus === 'pending' && (
          <Card className="border-amber-200 mt-2">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                Rejection Reason (if rejecting)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                className="w-full border rounded-lg p-3 text-sm min-h-[100px] resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </CardContent>
          </Card>
        )}

        {/* Approval/Rejection Status */}
        {approvalStatus !== 'pending' && (
          <Card className={approvalStatus === 'approved' ? 'border-green-200 bg-green-50 mt-6' : 'border-red-200 bg-red-50 mt-6'}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                {approvalStatus === 'approved' ? (
                  <Check className="h-5 w-5 text-green-600 mt-0.5" />
                ) : (
                  <X className="h-5 w-5 text-red-600 mt-0.5" />
                )}
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Tour {approvalStatus === 'approved' ? 'Approved' : 'Rejected'}
                  </h3>
                  <p className="text-xs text-gray-600 mt-1">
                    {approvalStatus === 'approved'
                      ? 'This tour has been approved and the tour operator has been notified.'
                      : `Rejection reason: ${rejectionReason}`
                    }
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
